import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  TableVirtuoso,
  TableComponents,
  TableVirtuosoHandle,
} from 'react-virtuoso';
import {
  Box,
  Button,
  Skeleton,
  SxProps,
  TableSortLabel,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Columns,
  DataFromColumns,
  TableData,
  TableSizeType,
  VirtualizedTableProps,
} from '../../../types/reports';
import { formatDollarAmount } from '../../../utils/global/helper';
import ReportBox from '../../reports/ReportBox';
import { Sort } from '../../../types/ui';
import TableImage from '../TableImage';
import { getTableCellPadding } from '../../../utils/table';
import { px } from '../../../utils/styles';
import TableDropdownMenu from '../../global/TableDropdownMenu';

const HEADER_HEIGHT = 90;
const COMPACT_HEADER_HEIGHT = 57;
const ROW_HEIGHT = 120;
const COMPACT_ROW_HEIGHT = 51;
const SKELETON_ROWS = 5;

function renderValue(
  value: TableData,
  column: Pick<Columns[number], 'dollar'>,
) {
  if (value === undefined || value === null) return null;
  return column.dollar && typeof value === 'number' ? (
    formatDollarAmount(Number(value))
  ) : typeof value === 'object' && 'imageUrl' in value ? (
    <TableImage
      badge={value.badge}
      needsAttention={value.needsAttention}
      image={value.imageUrl}
    />
  ) : typeof value === 'object' && 'actions' in value ? (
    <TableDropdownMenu options={value.actions} />
  ) : typeof value === 'object' && 'button' in value ? (
    <Button
      onClick={value.button.onClick}
      variant="outlined"
      className="table-button"
      disabled={value.button.disabled}>
      {value.button.label}
    </Button>
  ) : typeof value === 'object' && 'component' in value ? (
    value.component
  ) : (
    value
  );
}

const VirtuosoTableComponents: TableComponents<Record<string, TableData>> = {
  Scroller: React.forwardRef<HTMLDivElement, { sx?: SxProps<Theme> }>(
    ({ sx, ...props }, ref) => (
      <TableContainer
        component={Paper}
        {...props}
        ref={ref}
        sx={[
          { overscrollBehavior: 'contain' },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      />
    ),
  ),
  Table: props => (
    <Table
      {...props}
      sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }}
    />
  ),
  TableHead,
  TableBody: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
    <TableBody {...props} ref={ref} />
  )),
};

function HeaderCell({
  type,
  column,
  rows,
  sort,
}: {
  type: TableSizeType;
  column: Columns[number];
  rows: DataFromColumns<Columns>[];
  sort?: Sort;
}) {
  const theme = useTheme();

  const headerFontWeight = type === 'compact' ? 700 : 600;

  const canvasContext = React.useMemo(
    () => document.createElement('canvas').getContext('2d')!,
    [],
  );

  const headerFont = React.useMemo(
    () =>
      `${headerFontWeight} ${theme.typography.body2.fontSize} ${theme.typography.fontFamily}`,
    [
      headerFontWeight,
      theme.typography.body2.fontSize,
      theme.typography.fontFamily,
    ],
  );

  const bodyFont = React.useMemo(
    () =>
      `${theme.typography.body1.fontWeight} ${theme.typography.body1.fontSize} ${theme.typography.fontFamily}`,
    [
      theme.typography.body1.fontWeight,
      theme.typography.body1.fontSize,
      theme.typography.fontFamily,
    ],
  );

  const measureTextWidth = React.useCallback(
    (text: string, header?: boolean): number => {
      canvasContext.font = header ? headerFont : bodyFont;
      return canvasContext.measureText(text).width;
    },
    [canvasContext, headerFont, bodyFont],
  );

  const align = column.numeric || column.dollar ? 'right' : 'left';
  const justifyContent = align === 'right' ? 'flex-end' : 'flex-start';
  const isKeySortable = sort?.sortableKeys?.includes(column.dataKey);
  const padding = getTableCellPadding(type);

  const autoWidth = React.useMemo(() => {
    if (column.width !== 'auto') return 0;

    const headerWidth = measureTextWidth(column.label, true);

    let maxDataWidth = 0;
    // eslint-disable-next-line no-restricted-syntax
    for (const row of rows) {
      const columnValue = renderValue(row[column.dataKey], {
        dollar: column.dollar,
      });
      if (!columnValue) continue;
      const textWidth = measureTextWidth(String(columnValue));
      if (textWidth > maxDataWidth) {
        maxDataWidth = textWidth;
      }
    }

    const horizontalPadding = padding.x * 2;
    const sortWidth = isKeySortable ? 32 : 0;
    const tooltipPadding = column.tooltip ? 32 : 0;
    const headerTotalWidth = headerWidth + sortWidth + tooltipPadding;
    const contentWidth = Math.max(headerTotalWidth, maxDataWidth);

    return contentWidth + horizontalPadding + (column.extraWidth ?? 0);
  }, [
    column.extraWidth,
    column.width,
    column.label,
    column.tooltip,
    column.dataKey,
    column.dollar,
    measureTextWidth,
    padding.x,
    isKeySortable,
    rows,
  ]);

  const width = column.width === 'auto' ? autoWidth : column.width;

  const onSort = (orderBy: string) => {
    if (!sort) return;
    sort.onSort(orderBy);
  };

  return (
    <TableCell
      key={column.dataKey}
      variant="head"
      align={align}
      sx={{
        backgroundColor: 'background.paper',
        fontWeight: headerFontWeight,
        fontSize: theme.typography.body2.fontSize,
        fontFamily: theme.typography.body2.fontFamily,
        width,
        boxSizing: 'border-box',
        '& .MuiTableSortLabel-icon': {
          display: isKeySortable ? 'inline-block' : 'none',
        },
        px: px(padding.x),
        py: px(padding.x),
      }}>
      <Box
        component="span"
        display="flex"
        alignItems="center"
        justifyContent={justifyContent}
        gap={0.5}>
        <TableSortLabel
          disabled={!sort || !sort.sortableKeys?.includes(column.dataKey)}
          active={column.dataKey === sort?.orderBy}
          direction={column.dataKey === sort?.orderBy ? sort.order : 'asc'}
          onClick={() => onSort(column.dataKey)}>
          <span>{column.label}</span>
        </TableSortLabel>
        {column.tooltip && (
          <Tooltip title={column.tooltip} arrow placement="top">
            <Button
              variant="text"
              color="inherit"
              sx={{
                padding: 0,
                border: 'none',
                width: 'auto',
                minWidth: 0,
                flexShrink: 1,
                ':hover': {
                  backgroundColor: 'transparent',
                },
              }}>
              <InfoOutlinedIcon fontSize="small" />
            </Button>
          </Tooltip>
        )}
      </Box>
    </TableCell>
  );
}

function FixedHeaderContent({
  columns,
  rows,
  sort,
  type,
}: {
  columns: Columns;
  rows: DataFromColumns<Columns>[];
  sort?: Sort;
  type: TableSizeType;
}) {
  return (
    <TableRow
      sx={{
        height: type === 'compact' ? COMPACT_HEADER_HEIGHT : HEADER_HEIGHT,
      }}>
      {columns.map(column => (
        <HeaderCell
          key={column.dataKey}
          type={type}
          column={column}
          rows={rows}
          sort={sort}
        />
      ))}
    </TableRow>
  );
}

function rowContent(
  _index: number,
  row: Record<string, TableData>,
  columns: Columns,
  type: TableSizeType,
) {
  const padding = getTableCellPadding(type);
  return (
    // eslint-disable-next-line react/jsx-fragments
    <React.Fragment>
      {columns.map(column => {
        const value = row[column.dataKey];
        return (
          <TableCell
            key={column.dataKey}
            align={column.numeric || column.dollar ? 'right' : 'left'}
            style={{
              height: type === 'compact' ? COMPACT_ROW_HEIGHT : ROW_HEIGHT,
              boxSizing: 'border-box',
            }}
            sx={{
              // truncate with ellipsis
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              px: px(padding.x),
              py: px(padding.y),
            }}>
            <Typography
              variant={type === 'compact' ? 'body2' : 'body1'}
              component="span">
              {renderValue(value, column)}
            </Typography>
          </TableCell>
        );
      })}
    </React.Fragment>
  );
}

function skeletonContent(columns: Columns, type: TableSizeType) {
  return (
    <TableCell
      colSpan={columns.length}
      sx={{
        height: type === 'compact' ? COMPACT_ROW_HEIGHT : ROW_HEIGHT,
        width: '100%',
        boxSizing: 'border-box',
      }}>
      <Skeleton
        variant="rounded"
        width="100%"
        height={type === 'compact' ? '100%' : COMPACT_ROW_HEIGHT}
      />
    </TableCell>
  );
}

function emptyContent(
  emptyText: string,
  columns: Columns,
  type: TableSizeType,
) {
  return (
    <TableCell
      colSpan={columns.length}
      sx={{
        height: type === 'compact' ? COMPACT_ROW_HEIGHT : ROW_HEIGHT,
        width: '100%',
        boxSizing: 'border-box',
      }}>
      <Typography variant="body2">{emptyText}</Typography>
    </TableCell>
  );
}

export default function VirtualizedTable<ColumnsType extends Columns>({
  height: tableHeight = 400,
  sort: sortProp,
  data,
  onRowClick,
  columns: baseColumns,
  headerContent,
  isLoading,
  emptyText = 'No data available',
  onEndReached,
  type,
  resetScrollKey,
}: VirtualizedTableProps<ColumnsType>) {
  const theme = useTheme();
  const columns = baseColumns.filter(column => !column.hidden);

  const containerRef = React.useRef<TableVirtuosoHandle>(null);

  React.useEffect(() => {
    if (resetScrollKey === undefined) return;
    containerRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  }, [resetScrollKey]);

  const rows = React.useMemo(
    () =>
      data.map(row =>
        columns.reduce(
          (acc, column) => ({
            ...acc,
            [column.dataKey]: row[column.dataKey as keyof typeof row],
          }),
          {} as Record<string, TableData>,
        ),
      ),
    [columns, data],
  );

  const Components = React.useMemo<TableComponents<Record<string, TableData>>>(
    () => ({
      ...VirtuosoTableComponents,
      // eslint-disable-next-line react/no-unstable-nested-components, arrow-body-style
      TableRow: ({ item: _item, ...props }) => {
        const row = data[props['data-index']];
        const hasClick = onRowClick && row && !!rows.length;
        return (
          <TableRow
            {...props}
            onClick={hasClick ? () => onRowClick(row) : undefined}
            sx={{
              cursor: hasClick ? 'pointer' : 'default',
              ':hover': {
                backgroundColor: hasClick
                  ? theme.palette.lightGrey.main
                  : 'transparent',
              },
            }}
          />
        );
      },
    }),
    [data, onRowClick, theme.palette.lightGrey.main, rows.length],
  );

  const sort = React.useMemo(() => {
    if (!sortProp) return undefined;
    return {
      ...sortProp,
      onSort: (orderBy: string) => {
        containerRef.current?.scrollTo({
          top: 0,
          // Valid CSSOM, but missing from ScrollBehavior in this TS lib version.
          behavior: 'instant' as unknown as ScrollBehavior,
        });
        sortProp.onSort(orderBy);
      },
    };
  }, [sortProp]);

  const endReachedCalledRef = React.useRef(false);
  const wrappedOnEndReached = React.useCallback(async () => {
    if (!onEndReached || endReachedCalledRef.current) return;

    endReachedCalledRef.current = true;

    await onEndReached();

    setTimeout(() => {
      endReachedCalledRef.current = false;
    }, 0);
  }, [onEndReached]);

  // will always be 1+
  const rowCount = Math.max(1, isLoading ? SKELETON_ROWS : rows.length);
  const rowHeight = type === 'compact' ? COMPACT_ROW_HEIGHT : ROW_HEIGHT;
  const headerHeight =
    type === 'compact' ? COMPACT_HEADER_HEIGHT : HEADER_HEIGHT;
  const rowsHeightWithHeader =
    rowCount * rowHeight + (data.length ? headerHeight : 0);

  // prevent white space at the bottom of the table
  const dynamicHeight =
    rowsHeightWithHeader > tableHeight ? tableHeight : rowsHeightWithHeader;

  return (
    <ReportBox
      headerContent={headerContent}
      isLoading={isLoading}
      contentBoxStyle={{ height: dynamicHeight }}
      type={type}>
      <TableVirtuoso
        ref={containerRef}
        data={
          // eslint-disable-next-line no-nested-ternary
          isLoading
            ? Array.from({ length: SKELETON_ROWS }).map(() => ({}))
            : !rows.length
            ? [{}]
            : rows
        }
        components={Components}
        fixedHeaderContent={() =>
          data.length ? FixedHeaderContent({ columns, rows, sort, type }) : null
        }
        itemContent={(_index, item) => {
          if (isLoading) {
            return skeletonContent(columns, type);
          }
          if (!rows.length) {
            return emptyContent(emptyText, columns, type);
          }
          return rowContent(_index, item, columns, type);
        }}
        endReached={wrappedOnEndReached}
      />
    </ReportBox>
  );
}
